// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import siteMessage from "@insite/client-framework/SiteMessage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { getCookie } from "@insite/mobius/utilities/cookies";
import resetKeepMeLoggedInCookie from "@store/Framework/Store/Components/KeepMeLoggedInModal/Handlers/ResetKeepMeLoggedInCookie";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useSession, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useEffect, useRef, useState } from "react";

export interface KeepMeLoggedInModalProps extends ExpandStyles<KeepMeLoggedInModalStyles> {}

export interface KeepMeLoggedInModalStyles {
    keepMeLoggedInModal?: TwoButtonModalStyles;
}

export const keepMeLoggedInModalStyles: KeepMeLoggedInModalStyles = {};

const keepMeLoggedInIntervalDelay = 10000;
const storefrontSessionCookieName = "StorefrontSessionExpiration";

const KeepMeLoggedInModal: FC<KeepMeLoggedInModalProps> = ({ extendedStyles, overridedStyles }) => {
    const styles = useExpandProps(keepMeLoggedInModalStyles, extendedStyles, overridedStyles);
    const [showKeepMeLoggedInModal, setShowKeepMeLoggedInModal] = useState(false);
    const {
        extensionWebsiteSettings: { enableSessionTimeoutWarningModal, sessionTimeoutWarningModalTimeRemaining },
    } = useSettingsCollection();
    const keepModalClosedRef = useRef(false);
    const dispatch = useAppDispatch();
    const session = useSession();
    const toaster = useToaster();

    const keepMeLoggedInHandler = () => {
        setShowKeepMeLoggedInModal(false);
        dispatch(resetKeepMeLoggedInCookie());
    };

    const signOutHandler = () => {
        dispatch(signOut());
    };

    const closeHandler = () => {
        keepModalClosedRef.current = true;
        setShowKeepMeLoggedInModal(false);
    };

    useEffect(() => {
        if (
            !enableSessionTimeoutWarningModal ||
            !sessionTimeoutWarningModalTimeRemaining ||
            !session.isAuthenticated ||
            session.isGuest
        ) {
            return;
        }

        const keepMeLoggedInInterval = setInterval(() => {
            const storefrontSessionExpirationCookie = getCookie(storefrontSessionCookieName);

            if (!storefrontSessionExpirationCookie) {
                return;
            }

            const timeDifferences = Number(storefrontSessionExpirationCookie) - Date.now();

            if (timeDifferences <= 0) {
                signOutHandler();
                return;
            }

            if (timeDifferences <= keepMeLoggedInIntervalDelay) {
                toaster.addToast({
                    body: siteMessage("Keep_Me_Logged_In_Toaster_Message"),
                    messageType: "warning",
                    timeoutLength: keepMeLoggedInIntervalDelay,
                });
                return;
            }

            if (timeDifferences <= sessionTimeoutWarningModalTimeRemaining && !keepModalClosedRef.current) {
                setShowKeepMeLoggedInModal(true);
            }
        }, keepMeLoggedInIntervalDelay);

        return () => {
            clearInterval(keepMeLoggedInInterval);
        };
    }, [enableSessionTimeoutWarningModal, session.isAuthenticated]);

    return (
        <TwoButtonModal
            {...styles.keepMeLoggedInModal}
            modalIsOpen={showKeepMeLoggedInModal}
            headlineText={translate("Warning")}
            messageText={siteMessage(
                "Keep_Me_Logged_In_Modal_Message",
                sessionTimeoutWarningModalTimeRemaining
                    ? (sessionTimeoutWarningModalTimeRemaining / 1000 / 60).toString()
                    : "",
            )}
            cancelButtonText={translate("Close")}
            submitButtonText={translate("Keep Me Logged In")}
            onCancel={closeHandler}
            onSubmit={keepMeLoggedInHandler}
        />
    );
};

export default KeepMeLoggedInModal;
